import { isMockServices } from 'src/utils';
import { getCompatibleAuthTokenHttp } from './utils/enhancedAuthTokenHttp';
import mock from './utils/mock';
import { MosError } from 'src/utils/errors/MosError';

export interface ReportColumnDefinition {
  key: string;
  label: string;
  type: string;
}

export interface Report {
  id: string;
  reportName: string;
  metadata?: {
    filters?: string[];
  };
  columnDefinitions: ReportColumnDefinition[];
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export const getReports = async (): Promise<Report[]> => {
  if (isMockServices()) return await mock(_mockReports, { delay: 500 });

  const authTokenHttp = await getCompatibleAuthTokenHttp();
  const response = await authTokenHttp<{ data: Report[] }>('/reports', {
    method: 'GET',
  });
  if (response.data?.data) return response.data.data;
  throw new Error('Failed to fetch reports');
};

const _mockReports: Report[] = [
  {
    id: '1',
    reportName: 'Bank Statement Analysis',
    metadata: {
      filters: ['loanNumbers', 'dateFrom', 'dateTo'],
    },
    columnDefinitions: [
      { key: 'loanNumber', label: 'Loan Number', type: 'string' },
      { key: 'depositAmount', label: 'Deposit Amount', type: 'number' },
    ],
    active: true,
    createdAt: '2026-03-20',
    updatedAt: '2026-03-20',
  },
];

export const getReportData = async (
  reportName: string,
  filters?: Record<string, string>,
): Promise<Record<string, string>[]> => {
  if (isMockServices())
    return await mock(_mockReportData(reportName), { delay: 500 });

  const queryParams = new URLSearchParams();
  queryParams.append('reportName', reportName);

  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value) queryParams.append(key, value);
    });
  }

  const authTokenHttp = await getCompatibleAuthTokenHttp();
  const response = await authTokenHttp<{ data: Record<string, string>[] }>(
    `/reports/data?${queryParams.toString()}`,
    {
      method: 'GET',
    },
  );
  if (response.data?.data) return response.data.data;
  throw new Error('Failed to fetch report data');
};

const _secureRandomInt = (
  minInclusive: number,
  maxInclusive: number,
): number => {
  if (!Number.isInteger(minInclusive) || !Number.isInteger(maxInclusive)) {
    throw new MosError({ message: 'Random range must be integers' });
  }
  if (maxInclusive < minInclusive) {
    throw new MosError({ message: 'Invalid random range' });
  }

  const range = maxInclusive - minInclusive + 1;
  if (range <= 0) throw new MosError({ message: 'Invalid random range' });

  const cryptoObj = globalThis.crypto;
  if (!cryptoObj?.getRandomValues) {
    throw new MosError({ message: 'Secure RNG unavailable' });
  }

  const maxUint32 = 0xffffffff;
  const limit = maxUint32 - (maxUint32 % range);
  const buf = new Uint32Array(1);

  // Rejection sampling to avoid modulo bias.
  while (true) {
    cryptoObj.getRandomValues(buf);
    const x = buf[0];
    if (x < limit) return minInclusive + (x % range);
  }
};

const _mockReportData = (reportName: string): Record<string, string>[] => {
  if (reportName === 'Bank Statement Analysis') {
    return Array.from({ length: 25 }, (_, i) => ({
      loanNumber: `L-20${10 + i}`,
      depositAmount: _secureRandomInt(500, 8499).toString(),
    }));
  }

  if (reportName === 'Document Filing Status') {
    return Array.from({ length: 15 }, (_, i) => ({
      docId: `D-${402 + i}`,
      type: i % 2 === 0 ? 'W-2' : 'Bank Statement',
      status: i % 3 === 0 ? 'Review Needed' : 'Success',
      isTouchless: i % 3 === 0 ? 'No' : 'Yes',
    }));
  }

  return [];
};
